import { Card } from 'primereact/card';
import * as React from 'react';
import { ReactNode, useContext } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { Notation } from '../../../Vocabulary/components/Notation';
import { SolrEntity } from '../../../util/types';
import { Authorizer } from '../../../Auth/utils/Authorizer';
import { AuthContext } from '../../../Auth/components/AuthProvider';
import { Button } from 'primereact/button';
import { Link, NavigateFunction, useNavigate } from 'react-router';
import { ParsedMathJax } from '../../components/ParsedMathJax';
import { localDateTime } from '../../../util/intl.utils';
import { Divider } from 'primereact/divider';

interface Props {
  position: number;
  header: SearchResultHeader;
  footer?: ReactNode;
  contents: (ReactNode | ReactNode[])[];
  result: SolrEntity;
  editUrl?: string;
  requestDelete?: () => void;
  delete?: () => void;
}

export interface SearchResultHeader {
  label: string;
  url: string;
}

export const SearchResult = (props: Props): ReactNode => {
  const intl: IntlShape = useIntl();
  const authorizer: Authorizer = useContext(AuthContext).authorizer;
  const navigate: NavigateFunction = useNavigate();


  return <Card>
    <div className={'flex xl:flex-row xl:justify-content-between flex-column gap-2 xl:gap-2'}>
      <div className={'flex flex-grow-1 flex-column gap-2'}>
        <div className={'flex justify-content-between align-items-start gap-4'}>
          <Link className={'block no-underline text-primary sm:text-base text-sm'} to={props.header.url}>
            <h3 className={'no-underline text-lgl mt-1 mb-1'}><ParsedMathJax content={props.header.label} /></h3>
          </Link>
          <div className={'flex justify-content-start xl:justify-content-end align-items-end'}>#{props.position}</div>
        </div>
        <div className={'flex flex-column justify-content-between md:flex-row'}>
      <div className={'flex flex-column gap-1'}>
        {props.contents.map((content: ReactNode, index: number): ReactNode => (
          <div key={`content-${index}`} className={'mb-0'}>{content}</div>
        ))}
      </div>
      <div className={'flex flex-column text-600 text-xs'}>
        <div className="flex md:justify-content-end white-space-nowrap">
          {`Id: ${props.result.id}`}
        </div>
        <div className="flex md:justify-content-end white-space-nowrap">
          <FormattedMessage id="entity.attr.editorialStatus"/>: <Notation v="editorial_status_list"
                                                                          k={props.result.editorialStatus}/>
        </div>
        <div className="flex md:justify-content-end white-space-nowrap">
          <FormattedMessage
            id="entity.attr.recordCreationDate"/>: {localDateTime(props.result.recordCreationDate, intl.locale)}
        </div>
        <div className="flex md:justify-content-end white-space-nowrap">
          <FormattedMessage
            id="entity.attr.recordChangeDate"/>: {localDateTime(props.result.recordChangeDate, intl.locale)}
        </div>
      </div>
    </div>
      </div>
      <Divider layout={'vertical'} className={'hidden xl:block mx-0'} />
      <Divider className={'xl:hidden block my-0'} />
      <div className={'flex flex-row justify-content-end xl:flex-column w-full xl:w-2rem align-content-end gap-2'}>
        <div>{props.footer}</div>
        {authorizer.hasAnyOfRoles(['ROLE_EDITOR', 'ROLE_ADMIN']) &&
          <Button icon={'pi pi-pencil'} onClick={(): void | Promise<void> => navigate(props.editUrl)}
                  tooltip={intl.formatMessage({id: 'button.edit'})}
                  tooltipOptions={{position: 'bottom'}}
                  outlined={true}></Button>}
        {authorizer.hasAnyOfRoles(['ROLE_EDITOR']) &&
          <Button icon={'pi pi-trash'} severity={'warning'} onClick={() => props.requestDelete()}
                  tooltip={intl.formatMessage({id: 'button.deleteRequest'})}
                  tooltipOptions={{position: 'bottom'}}
                  outlined={true}></Button>
        }
        {authorizer.hasAnyOfRoles(['ROLE_ADMIN']) &&
          <Button icon={'pi pi-trash'} severity={'danger'} onClick={() => props.delete()}
                  tooltip={intl.formatMessage({id: 'button.delete'})}
                  tooltipOptions={{position: 'bottom'}}
                  outlined={true}></Button>
        }
      </div>
    </div>
  </Card>;
};
